import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, AlertTriangle, CalendarClock, RefreshCw, Plus, Bell, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PRODUCT_TYPES = [
  { value: "motor", label: "Motor" },
  { value: "health", label: "Health" },
  { value: "life", label: "Life" },
  { value: "property", label: "Property" },
  { value: "travel", label: "Travel" },
  { value: "marine", label: "Marine" },
  { value: "liability", label: "Liability" },
  { value: "other", label: "Other" },
];

const STATUS_TONE = {
  active:         { bg: "#DCFCE7", fg: "#15803D", label: "Active" },
  expiring_soon:  { bg: "#FEF3C7", fg: "#B45309", label: "Expiring soon" },
  overdue:        { bg: "#FEE2E2", fg: "#B91C1C", label: "Overdue" },
  renewed:        { bg: "#DBEAFE", fg: "#1D4ED8", label: "Renewed" },
  lapsed:         { bg: "#E5E7EB", fg: "#374151", label: "Lapsed" },
  cancelled:      { bg: "#F1F5F9", fg: "#475569", label: "Cancelled" },
};

const BUCKET_META = [
  { key: "overdue", label: "Overdue",  accent: "#B91C1C" },
  { key: "t_7",     label: "Due in 7d", accent: "#F59E0B" },
  { key: "t_15",    label: "Due in 15d",accent: "#F59E0B" },
  { key: "t_30",    label: "Due in 30d",accent: "#0EA5FF" },
  { key: "t_60",    label: "Due in 60d",accent: "#0EA5FF" },
  { key: "not_yet", label: "Later",     accent: "#5FBF3A" },
];

const MANAGE_ROLES = ["super_admin", "founder", "ops_manager", "hr_admin", "wealth_executive"];

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
const dateStr = (v) => v ? new Date(v).toLocaleDateString() : "—";

export default function Insurance() {
  const { user } = useAuth();
  const canManage = user && MANAGE_ROLES.includes(user.role);
  const [dash, setDash] = useState(null);
  const [q, setQ] = useState("");
  const [productType, setProductType] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [detail, setDetail] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const loadDash = async () => {
    const r = await api.get("/insurance/dashboard");
    setDash(r.data);
  };
  useEffect(() => { loadDash(); }, []);

  const [policies, setPolicies] = useState([]);
  const loadPolicies = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (productType !== "all") params.product_type = productType;
    if (statusF !== "all") params.status = statusF;
    const r = await api.get("/insurance/policies", { params });
    setPolicies(r.data);
  }, [q, productType, statusF]);
  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/insurance/reminders/run");
      const total = Object.values(r.data.created || {}).reduce((s, n) => s + n, 0);
      toast.success(`Reminder scan complete — ${total} new reminders`);
      await loadDash();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  return (
    <AppLayout title="Insurance">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard icon={ShieldCheck}   label="Active policies"  value={dash?.total_active}    accent="#0EA5FF"/>
        <StatCard icon={CalendarClock} label="Expiring in 30d"  value={dash?.expiring_30}     accent="#F59E0B"/>
        <StatCard icon={AlertTriangle} label="Due this week"    value={dash?.due_this_week}   accent="#B91C1C"/>
        <StatCard icon={Bell}          label="Pending reminders"value={dash?.pending_reminders} accent="#5FBF3A"/>
      </div>

      {/* Filter + actions bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="text-[var(--miro-text-muted)]"/>
          <input data-testid="insurance-search" className="bg-transparent outline-none text-sm flex-1"
                 placeholder="Search customer / policy / provider…" value={q} onChange={e => setQ(e.target.value)}/>
        </div>
        <Select value={productType} onValueChange={setProductType}>
          <SelectTrigger data-testid="insurance-filter-product" className="w-40 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {PRODUCT_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="insurance-filter-status" className="w-40 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_TONE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button data-testid="insurance-run-scan" variant="outline" onClick={runScan} disabled={scanning}>
            <RefreshCw size={14} className={`mr-1 ${scanning ? "animate-spin" : ""}`}/>
            {scanning ? "Running…" : "Run scan"}
          </Button>
          {canManage && (
            <NewPolicyDialog open={newOpen} setOpen={setNewOpen} onCreated={() => { loadDash(); loadPolicies(); }}/>
          )}
        </div>
      </div>

      <Tabs defaultValue="buckets">
        <TabsList data-testid="insurance-tabs" className="bg-white border border-[var(--miro-border)]">
          <TabsTrigger value="buckets">Renewal buckets</TabsTrigger>
          <TabsTrigger value="all">All policies</TabsTrigger>
        </TabsList>

        <TabsContent value="buckets" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {BUCKET_META.map(b => {
              const list = dash?.buckets?.[b.key] || [];
              return (
                <div key={b.key} data-testid={`insurance-bucket-${b.key}`}
                     className="bg-white border border-[var(--miro-border)] rounded-xl p-4 flex flex-col min-h-[220px]">
                  <div className="flex items-center justify-between">
                    <div className="miro-overline">{b.label}</div>
                    <div className="text-sm font-mono font-semibold" style={{ color: b.accent }}>{list.length}</div>
                  </div>
                  <div className="mt-3 space-y-1.5 overflow-y-auto max-h-[280px]">
                    {list.length === 0 && <div className="text-xs text-[var(--miro-text-muted)]">Nothing here.</div>}
                    {list.map(p => (
                      <button key={p.id} data-testid={`bucket-policy-${p.id}`}
                              onClick={() => setDetail(p)}
                              className="w-full text-left rounded-lg border border-[var(--miro-border)] p-2 hover:bg-[var(--miro-bg)] transition-colors">
                        <div className="font-semibold text-[var(--miro-navy)] text-sm truncate">{p.customer_name}</div>
                        <div className="text-[11px] text-[var(--miro-text-muted)] truncate">{p.policy_number} · {p.provider}</div>
                        <div className="text-[11px] text-[var(--miro-text-secondary)]">Exp {dateStr(p.expiry_date)} · {inr(p.premium)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <PoliciesTable policies={policies} onOpen={setDetail}/>
        </TabsContent>
      </Tabs>

      <PolicyDrawer policy={detail} onClose={() => setDetail(null)}
                    canManage={canManage}
                    onChanged={() => { loadDash(); loadPolicies(); setDetail(null); }}/>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="miro-card p-5">
      <div className="flex items-start justify-between">
        <div className="miro-overline">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}18`, color: accent }}>
          <Icon size={17}/>
        </div>
      </div>
      <div className="font-display font-black text-3xl mt-3 text-[var(--miro-navy)]">{value ?? "—"}</div>
    </div>
  );
}

function PoliciesTable({ policies, onOpen }) {
  return (
    <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
      <table data-testid="policies-table" className="w-full text-sm">
        <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
          <tr className="text-left">
            <th className="px-4 py-3 font-semibold">Customer</th>
            <th className="px-4 py-3 font-semibold">Policy</th>
            <th className="px-4 py-3 font-semibold">Type</th>
            <th className="px-4 py-3 font-semibold">Expiry</th>
            <th className="px-4 py-3 font-semibold">Premium</th>
            <th className="px-4 py-3 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {policies.length === 0 && (<tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No policies match your filters.</td></tr>)}
          {policies.map(p => {
            const tone = STATUS_TONE[p.status] || STATUS_TONE.active;
            return (
              <tr key={p.id} data-testid={`policy-row-${p.id}`}
                  onClick={() => onOpen(p)}
                  className="border-t border-[var(--miro-border)] cursor-pointer hover:bg-[var(--miro-bg)] transition-colors">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--miro-navy)]">{p.customer_name}</div>
                  <div className="text-[11px] text-[var(--miro-text-muted)]">{p.customer_phone || "—"} · {p.customer_email || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs">{p.policy_number}</div>
                  <div className="text-[11px] text-[var(--miro-text-muted)]">{p.provider}</div>
                </td>
                <td className="px-4 py-3 capitalize text-[var(--miro-text-secondary)]">{p.product_type}</td>
                <td className="px-4 py-3 text-xs">{dateStr(p.expiry_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{inr(p.premium)}</td>
                <td className="px-4 py-3">
                  <Badge className="border-0 font-semibold text-[10px]" style={{ background: tone.bg, color: tone.fg }}>{tone.label}</Badge>
                </td>
              </tr>);
          })}
        </tbody>
      </table>
    </div>
  );
}

function NewPolicyDialog({ open, setOpen, onCreated }) {
  const empty = {
    customer_name: "", customer_phone: "", customer_email: "",
    policy_number: "", provider: "", product_type: "motor",
    plan_name: "", expiry_date: "", premium: 0, sum_assured: 0,
    notes: "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/insurance/policies", {
        ...f, premium: Number(f.premium) || 0, sum_assured: Number(f.sum_assured) || 0,
        customer_email: f.customer_email || null,
      });
      toast.success("Policy created");
      setOpen(false); setF(empty); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-policy-btn" style={{ background: "var(--miro-navy)" }}>
          <Plus size={16} className="mr-1"/> New policy
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New policy</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Customer name *</Label><Input data-testid="new-policy-customer" value={f.customer_name} onChange={e => upd("customer_name", e.target.value)} className="mt-1"/></div>
          <div><Label>Phone</Label><Input value={f.customer_phone} onChange={e => upd("customer_phone", e.target.value)} className="mt-1"/></div>
          <div><Label>Email</Label><Input type="email" value={f.customer_email} onChange={e => upd("customer_email", e.target.value)} className="mt-1"/></div>
          <div><Label>Policy number *</Label><Input data-testid="new-policy-number" value={f.policy_number} onChange={e => upd("policy_number", e.target.value)} className="mt-1"/></div>
          <div><Label>Provider *</Label><Input data-testid="new-policy-provider" value={f.provider} onChange={e => upd("provider", e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Product type *</Label>
            <Select value={f.product_type} onValueChange={v => upd("product_type", v)}>
              <SelectTrigger data-testid="new-policy-type" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PRODUCT_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Plan name</Label><Input value={f.plan_name} onChange={e => upd("plan_name", e.target.value)} className="mt-1"/></div>
          <div><Label>Expiry date *</Label><Input data-testid="new-policy-expiry" type="date" value={f.expiry_date} onChange={e => upd("expiry_date", e.target.value)} className="mt-1"/></div>
          <div><Label>Premium (₹)</Label><Input type="number" min={0} value={f.premium} onChange={e => upd("premium", e.target.value)} className="mt-1"/></div>
          <div><Label>Sum assured (₹)</Label><Input type="number" min={0} value={f.sum_assured} onChange={e => upd("sum_assured", e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={e => upd("notes", e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="new-policy-submit"
                  disabled={!f.customer_name.trim() || !f.policy_number.trim() || !f.provider.trim() || !f.expiry_date || saving}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDrawer({ policy, onClose, canManage, onChanged }) {
  const [reminders, setReminders] = useState([]);
  useEffect(() => {
    if (!policy?.id) return;
    api.get("/insurance/reminders", { params: { policy_id: policy.id } }).then(r => setReminders(r.data));
  }, [policy?.id]);
  if (!policy) return null;
  const tone = STATUS_TONE[policy.status] || STATUS_TONE.active;

  const markRenewed = async () => {
    const newNum = window.prompt("New policy number (optional):", "");
    const newExp = window.prompt("New expiry date (YYYY-MM-DD, optional):", "");
    try {
      const params = {};
      if (newNum) params.new_policy_number = newNum;
      if (newExp) params.new_expiry = new Date(newExp).toISOString();
      await api.post(`/insurance/policies/${policy.id}/renew`, null, { params });
      toast.success("Policy marked renewed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Renew failed"); }
  };

  const acknowledgeReminder = async (r) => {
    try {
      await api.post(`/insurance/reminders/${r.id}/acknowledge`);
      toast.success("Reminder acknowledged");
      const rr = await api.get("/insurance/reminders", { params: { policy_id: policy.id } });
      setReminders(rr.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="miro-overline">{policy.policy_number}</div>
              <SheetTitle className="font-display text-2xl text-[var(--miro-navy)] leading-tight mt-1">
                {policy.customer_name}
              </SheetTitle>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge className="border-0 font-semibold text-[10px]" style={{ background: tone.bg, color: tone.fg }}>{tone.label}</Badge>
                <Badge className="border-0 font-semibold text-[10px] capitalize" style={{ background: "#E0F2FE", color: "#0284C7" }}>{policy.product_type}</Badge>
              </div>
            </div>
            {canManage && policy.status !== "renewed" && (
              <Button size="sm" data-testid="policy-mark-renewed" onClick={markRenewed} style={{ background: "var(--miro-navy)" }}>Mark renewed</Button>
            )}
          </div>
        </SheetHeader>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <F label="Provider" v={policy.provider}/>
          <F label="Plan" v={policy.plan_name || "—"}/>
          <F label="Start" v={dateStr(policy.start_date)}/>
          <F label="Expiry" v={dateStr(policy.expiry_date)}/>
          <F label="Premium" v={inr(policy.premium)}/>
          <F label="Sum assured" v={inr(policy.sum_assured)}/>
          <F label="Phone" v={policy.customer_phone || "—"}/>
          <F label="Email" v={policy.customer_email || "—"}/>
          {policy.notes && <div className="col-span-2"><div className="miro-overline">Notes</div><div className="text-[var(--miro-text-secondary)] mt-1">{policy.notes}</div></div>}
        </div>

        <div className="mt-6">
          <div className="miro-overline mb-2">Reminder log ({reminders.length})</div>
          {reminders.length === 0 && <div className="text-[var(--miro-text-muted)] text-sm">No reminders yet. Trigger a scan or wait for the automatic 6-hour cycle.</div>}
          <div className="space-y-2">
            {reminders.map(r => (
              <div key={r.id} data-testid={`reminder-row-${r.id}`}
                   className="border border-[var(--miro-border)] rounded-lg p-3 bg-white flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center flex-shrink-0">
                  <Bell size={15}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[var(--miro-navy)]">
                    T-{r.window_days === 0 ? "OVERDUE" : `${r.window_days}d`} · <span className="font-mono text-[10px] text-[var(--miro-text-muted)]">{new Date(r.triggered_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-[var(--miro-text-secondary)] mt-0.5">{r.message}</div>
                </div>
                {!r.acknowledged ? (
                  <Button size="sm" variant="outline" data-testid={`reminder-ack-${r.id}`} onClick={() => acknowledgeReminder(r)}>Ack</Button>
                ) : (
                  <span className="text-[10px] text-green-700 font-semibold">Ack'd</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function F({ label, v }) {
  return (
    <div>
      <div className="miro-overline">{label}</div>
      <div className="text-[var(--miro-navy)] font-medium mt-0.5">{v}</div>
    </div>
  );
}
